import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    // If returning from Google OAuth callback, skip /me — AuthCallback handles it
    if (window.location.hash?.includes('access_token=')) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`${API_URL}/api/auth/me`, config);
      setUser(response.data);
    } catch (err) {
      setUser(null);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('access_token', access_token);
      setUser(userData);
      return userData;
    } catch (err) {
      const message = formatApiError(err.response?.data?.detail);
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/register`, { name, email, password, phone });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('access_token', access_token);
      setUser(userData);
      return userData;
    } catch (err) {
      const message = formatApiError(err.response?.data?.detail);
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('access_token');
      setUser(null);
    }
  };

  /**
   * Redirects the browser to the backend Google OAuth start endpoint.
   * The backend handles the full Google consent-screen flow and sends
   * the user back to /auth/callback with the JWT in the URL fragment.
   */
  const loginWithGoogle = () => {
    const currentPath = window.location.pathname + window.location.search;
    const redirectAfter =
      currentPath && currentPath !== '/login' && currentPath !== '/register'
        ? currentPath
        : '';

    // Backend kicks off Google consent screen
    window.location.href =
      `${API_URL}/api/auth/google?redirect_after=${encodeURIComponent(redirectAfter)}`;
  };

  /**
   * Called by AuthCallback after Google redirects back with the JWT in the hash.
   * Stores the token and fetches the user profile.
   */
  const processOAuthCallback = async (accessToken) => {
    localStorage.setItem('access_token', accessToken);
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setUser(response.data);
      return response.data;
    } catch (err) {
      localStorage.removeItem('access_token');
      const message = formatApiError(err.response?.data?.detail);
      setError(message);
      throw new Error(message);
    }
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    processOAuthCallback,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isEmployee: user?.role === 'employee',
    isApplicant: user?.role === 'applicant',
    isB2B: user?.role === 'b2b_partner'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default AuthContext;